import {ClientUserState} from '../../../shared/scripts/ClientUserState'
import {DiscordSnowflake} from '@colonist/types'
// eslint-disable-next-line max-len
import {AnalyticEventCategory, AnalyticEventType, ReactivationSequenceAnalytics, ReferralTypeEnum, ReactivationTypeEnum, RedeemFreeAvatarAnalytics, ShareActivityLinkAnalytics} from '../../../../shared/entity_datas/EntityDatas'
import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {CA} from '../../../shared/scripts/analytics/Analytics'
import {CustomLinkUtils} from '../../../shared/scripts/ui_utils/CustomLinkUtils'
import {ReferralProgramUtils} from '../../../shared/scripts/referral/ReferralProgramUtils'
import {ViewPopupRedeemFreeAvatar} from '../ui_popup/ViewPopupRedeemFreeAvatar'
import {ViewPopupRewardsProgram} from '../ui_popup/ViewPopupRewardsProgram'

export class UIReferralProgramController {
    private removeChangeListener: () => void
    private readonly referrerId: DiscordSnowflake | string
    private readonly referralOrReactivationType: unknown

    constructor(referrerId: DiscordSnowflake | string, referralOrReactivationType: unknown) {
        this.referrerId = referrerId
        this.referralOrReactivationType = referralOrReactivationType
    }

    async initReferralTracking() {
        if(this.referrerId == undefined) return
        if(this.referralOrReactivationType == undefined) return

        if(this.referrerId == 'bot') {
            const reactivationType = Number(this.referralOrReactivationType) as ReactivationTypeEnum

            this.sendBotReactivationAnalyticsAndTriggerAction(reactivationType)
            return
        }

        const customLink = this.referralOrReactivationType as string

        const referralType = CustomLinkUtils.extractReferralTypeFromCustomLink(customLink) as unknown as ReferralTypeEnum

        void ReferralProgramUtils.setUserReferrer(this.referrerId as DiscordSnowflake, undefined, referralType)
    }

    private sendBotReactivationAnalyticsAndTriggerAction(reactivationType: ReactivationTypeEnum) {
        this.removeChangeListener = ClientUserState.registerChangeListener(() => {
            // Defer execution to next tick to ensure assignment to this.removeChangeListener is complete
            setTimeout(async() => {
                if(ClientUserState.haveAbTestsLoaded == false) return

                this.removeChangeListener()

                const abTestReactivateDiscordInactiveUsers = ClientUserState.abTests.REACTIVATE_DISCORD_INACTIVE_USERS

                if(abTestReactivateDiscordInactiveUsers != undefined) {
                    const botShareActivityLinkAnalytics = UIReferralProgramController.getBotShareActivityLinkAnalytics(reactivationType, abTestReactivateDiscordInactiveUsers)
                    if(botShareActivityLinkAnalytics != undefined) {
                        const eventDataBotShareActivityLink: AjaxDataTypes.AnalyticData = {
                            type: AnalyticEventType.ReactivationAnalytics,
                            category: AnalyticEventCategory.BotShareActivityLink,
                            floatValue: botShareActivityLinkAnalytics,
                        }
                        void CA.sendAnalyticEvent(eventDataBotShareActivityLink)
                    }
                }

                await UIReferralProgramController.triggerSpecialAction(reactivationType, abTestReactivateDiscordInactiveUsers)
            }, 0)
        })
    }

    private static getBotShareActivityLinkAnalytics(reactivationType: ReactivationTypeEnum, abTest: string | undefined): ReactivationSequenceAnalytics | undefined {
        switch(reactivationType) {
            case ReactivationTypeEnum.Reactivation24HoursAfterLastActivityLaunch:
                return ReactivationSequenceAnalytics.Reactivation24HoursAfterLastActivityLaunch
            case ReactivationTypeEnum.Reactivation1WeekAfterLastActivityLaunch:
                return ReactivationSequenceAnalytics.Reactivation1WeekAfterLastActivityLaunch
            case ReactivationTypeEnum.Reactivation1MonthAfterLastActivityLaunch:
                return ReactivationSequenceAnalytics.Reactivation1MonthAfterLastActivityLaunch
            case ReactivationTypeEnum.Reactivation3MonthsAfterLastActivityLaunch:
                return ReactivationSequenceAnalytics.Reactivation3MonthsAfterLastActivityLaunch
            case ReactivationTypeEnum.Reactivation6MonthsAfterLastActivityLaunch:
                return ReactivationSequenceAnalytics.Reactivation6MonthsAfterLastActivityLaunch
            case ReactivationTypeEnum.Reactivation1YearAfterLastActivityLaunch:
                return ReactivationSequenceAnalytics.Reactivation1YearAfterLastActivityLaunch
            case ReactivationTypeEnum.Reactivation2YearsAfterLastActivityLaunch:
                return ReactivationSequenceAnalytics.Reactivation2YearsAfterLastActivityLaunch
            case ReactivationTypeEnum.ABTestStart:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return ReactivationSequenceAnalytics.ABTestGroupAStart
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return ReactivationSequenceAnalytics.ABTestGroupBStart
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return ReactivationSequenceAnalytics.ABTestGroupCStart
                return undefined
            case ReactivationTypeEnum.ABTestNow:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return ReactivationSequenceAnalytics.ABTestGroupANow
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return ReactivationSequenceAnalytics.ABTestGroupBNow
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return ReactivationSequenceAnalytics.ABTestGroupCNow
                return undefined
            case ReactivationTypeEnum.ABTest2D:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return ReactivationSequenceAnalytics.ABTestGroupA2D
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return ReactivationSequenceAnalytics.ABTestGroupB2D
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return ReactivationSequenceAnalytics.ABTestGroupC2D
                return undefined
            case ReactivationTypeEnum.ABTest4D:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return ReactivationSequenceAnalytics.ABTestGroupA4D
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return ReactivationSequenceAnalytics.ABTestGroupB4D
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return ReactivationSequenceAnalytics.ABTestGroupC4D
                return undefined
            case ReactivationTypeEnum.ABTest7D:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return ReactivationSequenceAnalytics.ABTestGroupA7D
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return ReactivationSequenceAnalytics.ABTestGroupB7D
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return ReactivationSequenceAnalytics.ABTestGroupC7D
                return undefined
            case ReactivationTypeEnum.ABTest15D:
                if(abTest == 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE') return ReactivationSequenceAnalytics.ABTestGroupA15D
                if(abTest == 'GROUP_B_PROMOTE_REFERRAL_PROGRAM') return ReactivationSequenceAnalytics.ABTestGroupB15D
                if(abTest == 'GROUP_C_GET_FREE_AVATAR') return ReactivationSequenceAnalytics.ABTestGroupC15D
                return undefined
            default:
                return undefined
        }
    }

    private static async triggerSpecialAction(reactivationType: ReactivationTypeEnum, abTest: string | undefined) {
        if([ReactivationTypeEnum.ABTestStart, ReactivationTypeEnum.ABTestNow, ReactivationTypeEnum.ABTest2D, ReactivationTypeEnum.ABTest4D, ReactivationTypeEnum.ABTest7D, ReactivationTypeEnum.ABTest15D].includes(reactivationType)) {
            switch(abTest) {
                case 'GROUP_A_YOU_HAVE_NOT_PLAYED_FOR_A_WHILE':
                    break
                case 'GROUP_B_PROMOTE_REFERRAL_PROGRAM':
                    new ViewPopupRewardsProgram(ShareActivityLinkAnalytics.ABTestReactivateUserBaseReferralProgramPopupTriggered, ShareActivityLinkAnalytics.ABTestReactivateUserBaseReferralProgramSuccessfullyShared)

                    const eventDataRewardsProgram: AjaxDataTypes.AnalyticData = {
                        type: AnalyticEventType.ReferralAnalytics,
                        category: AnalyticEventCategory.ShareActivityLink,
                        floatValue: ShareActivityLinkAnalytics.ABTestReactivateUserBaseReferralProgramPrePopupTriggered,
                    }
                    void CA.sendAnalyticEvent(eventDataRewardsProgram)
                    break
                case 'GROUP_C_GET_FREE_AVATAR':
                    new ViewPopupRedeemFreeAvatar()

                    const eventDataRedeemFreeAvatar: AjaxDataTypes.AnalyticData = {
                        type: AnalyticEventType.ReactivationAnalytics,
                        category: AnalyticEventCategory.ABTestRedeemFreeAvatar,
                        floatValue: RedeemFreeAvatarAnalytics.ABTestRedeemFreeAvatarPopupTriggered,
                    }
                    void CA.sendAnalyticEvent(eventDataRedeemFreeAvatar)
                    break
                default: break
            }
        }
    }
}
